import { nearbyLandmark } from "./landmarks";

/**
 * The Stays filters, on top of sort.
 *
 * Every section answers from data we hold, never from a guess:
 *  - stars and amenities come from the property record;
 *  - distance comes from the stay's coordinates and its city's landmark;
 *  - the nightly price, the meal plan and cancellation come from the quote the
 *    card is showing. Meal plan and cancellation only exist once dates are
 *    chosen, so the panel offers them only then.
 *
 * Area (Medina, Gueliz...) is deliberately absent. The only place a stay's
 * neighbourhood is written down is a sentence of prose, and "just outside the
 * medina walls" reads as medina to any keyword match. It needs a real field
 * first; until then the map's "Search this area" and the distance section do
 * that job honestly.
 */
export const STAY_AMENITIES = [
  "pool",
  "spa",
  "hammam",
  "parking",
  "shuttle",
  "ac",
  "restaurant",
  "family",
  "adults",
  "pets",
  "fitness",
  "wifi",
] as const;
export type StayAmenity = (typeof STAY_AMENITIES)[number];

/** Amenity keys as the property content stores them ("rooftop-pool", "free-private-parking"...). */
const AMENITY_RULES: Record<StayAmenity, RegExp> = {
  pool: /(^|-)pools?($|-)/,
  spa: /(^|-)spa($|-)/,
  hammam: /hammam/,
  parking: /parking/,
  shuttle: /shuttle|airport/,
  ac: /air-condition|climate/,
  restaurant: /restaurant/,
  // "family-run" describes the owners, not who the hotel suits.
  family: /family(?!-run)|kids/,
  adults: /adults-only/,
  pets: /(^|-)pets?($|-)/,
  fitness: /fitness|gym/,
  wifi: /wifi/,
};

export const NEAR_OPTIONS = [10, 20] as const;
export type NearMinutes = (typeof NEAR_OPTIONS)[number];

export const MEAL_PLANS = ["breakfast", "half", "all"] as const;
export type MealPlan = (typeof MEAL_PLANS)[number];

export interface StayFilters {
  /** Minimum star rating: 3 means 3 and up. */
  stars?: 3 | 4 | 5;
  /** Every one listed must be on the hotel. */
  amenities?: StayAmenity[];
  /** At most this many minutes' walk to the city's landmark. */
  near?: NearMinutes;
  /** At least this meal plan on the quoted rate. */
  meal?: MealPlan;
  freeCancel?: boolean;
}

/** URL params, as the router hands them over (strings or parsed numbers). */
export interface StayFilterSearch {
  stars?: string | number;
  amen?: string;
  near?: string | number;
  meal?: string;
  free_cancel?: string | number | boolean;
  /** Older links: `pool=1` and `breakfast=1` from the first version. */
  pool?: string | number | boolean;
  breakfast?: string | number | boolean;
}

const on = (v: unknown) => v === true || v === 1 || v === "1" || v === "true";

export function readStayFilters(search: StayFilterSearch): StayFilters {
  const stars = Number(search.stars);
  const near = Number(search.near);
  const wanted = new Set(String(search.amen ?? "").split(",").map((s) => s.trim()));
  if (on(search.pool)) wanted.add("pool");
  const amenities = STAY_AMENITIES.filter((a) => wanted.has(a));
  const meal = MEAL_PLANS.includes(search.meal as MealPlan)
    ? (search.meal as MealPlan)
    : on(search.breakfast)
      ? "breakfast"
      : undefined;
  return {
    ...(stars === 3 || stars === 4 || stars === 5 ? { stars } : {}),
    ...(amenities.length ? { amenities } : {}),
    ...(near === 10 || near === 20 ? { near } : {}),
    ...(meal ? { meal } : {}),
    ...(on(search.free_cancel) ? { freeCancel: true } : {}),
  };
}

/**
 * The params to write for a set of filters; `undefined` removes one.
 *
 * Numbers, not numeric strings: the router JSON-encodes a string that looks
 * like a number, so "5" would reach the address bar as `stars=%225%22`. The
 * old `pool` and `breakfast` params are always cleared: they are read above
 * and rewritten in the new shape.
 */
export function stayFilterParams(f: StayFilters | undefined): Record<string, number | string | undefined> {
  return {
    stars: f?.stars,
    amen: f?.amenities?.length ? STAY_AMENITIES.filter((a) => f.amenities!.includes(a)).join(",") : undefined,
    near: f?.near,
    meal: f?.meal,
    free_cancel: f?.freeCancel ? 1 : undefined,
    pool: undefined,
    breakfast: undefined,
  };
}

export function countStayFilters(f: StayFilters | undefined): number {
  if (!f) return 0;
  return (
    (f.stars ? 1 : 0) +
    (f.amenities?.length ?? 0) +
    (f.near ? 1 : 0) +
    (f.meal ? 1 : 0) +
    (f.freeCancel ? 1 : 0)
  );
}

/** Drops the two filters only a dated quote can answer. */
export function withoutDatedFilters(f: StayFilters): StayFilters {
  return { ...f, meal: undefined, freeCancel: undefined };
}

export function hasAmenity(amenities: { key: string }[] | null | undefined, a: StayAmenity): boolean {
  const rule = AMENITY_RULES[a];
  return (amenities ?? []).some((x) => rule.test(x.key));
}

/** Board codes by the least they include. */
const BOARD_LEVEL: Record<string, number> = { RO: 0, BB: 1, BI: 1, HB: 2, FB: 3, AI: 4 };
const MEAL_LEVEL: Record<MealPlan, number> = { breakfast: 1, half: 2, all: 4 };
function boardLevel(code: string | null | undefined, name: string | null | undefined): number {
  const byCode = code ? BOARD_LEVEL[code.toUpperCase()] : undefined;
  if (byCode !== undefined) return byCode;
  const n = (name ?? "").toLowerCase();
  if (/all[ -]inclusive/.test(n)) return 4;
  if (/full board/.test(n)) return 3;
  if (/half board/.test(n)) return 2;
  if (/breakfast/.test(n) && !/\b(no|without)\b[^,;]*breakfast|breakfast[^,;]*\bnot included\b/.test(n)) return 1;
  return 0;
}

export interface FilterableStay {
  citySlug: string;
  latitude: number | null;
  longitude: number | null;
  starRating: number | null;
  amenities: { key: string }[];
  price: {
    boardType?: string | null;
    boardName: string | null;
    cancellation: { refundable: boolean };
  } | null;
}

export function matchesStayFilters(s: FilterableStay, f: StayFilters): boolean {
  if (f.stars && (s.starRating ?? 0) < f.stars) return false;
  if (f.amenities?.some((a) => !hasAmenity(s.amenities, a))) return false;
  if (f.near) {
    const n = nearbyLandmark(s.citySlug, s.latitude, s.longitude);
    if (!n || n.mode !== "walk" || n.minutes > f.near) return false;
  }
  if (f.meal && (!s.price || boardLevel(s.price.boardType, s.price.boardName) < MEAL_LEVEL[f.meal])) return false;
  if (f.freeCancel && !s.price?.cancellation.refundable) return false;
  return true;
}

/** Nightly price in the guest's currency, or null when there is none to judge. */
export function inPriceRange(nightly: number | null, min?: number, max?: number): boolean {
  if (min == null && max == null) return true;
  if (nightly == null) return false;
  return (min == null || nightly >= min) && (max == null || nightly <= max);
}

/** The price slider's range on the stays pages: nights, not activity tickets. */
export function stayPriceBounds(currency: string): { min: number; max: number } {
  return { min: 0, max: currency === "MAD" ? 10000 : 1000 };
}
